<template>
    <div v-if="model">
        <rkm-area no-margin-top title="Filtros" type="fieldset">
            <rkm-form ref="Form" show-legend>
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.unidadesSolicitantes"
                            @change="loadDependentOptions"
                            default-option-text="Todas"
                            label="Unidade solicitante"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeSolicitanteId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.locaisAtendimento"
                            @change="reloadOptions"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.locaisColeta"
                            @change="reloadOptions"
                            default-option-text="Todos"
                            label="Local de coleta"
                            show-default-option
                            text-field="nome"
                            v-model="model.coletaInfraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.examesGrupos"
                            default-option-text="Todos"
                            label="Exame/Grupo"
                            show-default-option
                            v-model="model.exameGrupo"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.situacoes"
                            default-option-text="Todos"
                            label="Situação da confirmação"
                            show-default-option
                            v-model="model.situacao"
                        />
                    </rkm-col>

                    <rkm-col class="align-self-end" cols="3">
                        <rkm-button color="primary" @click="$refs.Form.validate(search)">
                            Buscar
                        </rkm-button>
                        <rkm-button @click="reset">Limpar</rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-row v-if="searched" class="mb-3 totalizadores">
            <rkm-col v-for="item in totalizerItems" :key="item.key" cols="3">
                <div class="totalizador">
                    <span>{{ item.label }}</span>
                    <strong>{{ totalizadores[item.key] || 0 }}</strong>
                </div>
            </rkm-col>
        </rkm-row>

        <rkm-area no-margin-top title="Resultado">
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :checked="allOptionalColumnsSelected"
                        @change="selectAllOptionalColumns"
                        class="mx-2 mb-2"
                    >
                        TODAS
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="!searched"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-data-table
                :disable-search="true"
                :fields="visibleFields"
                :repository="repositories.confirmacaoColeta"
                :request-params="requestParams"
                :search-table="searched"
                :tbody-tr-class="rowClass"
                hide-filters
                ref="DataTable"
                sort-by="data"
            >
                <template v-slot:cell(situacaoConfirmacao)="{ value }">
                    {{ situationLabel(value) }}
                </template>

                <template v-slot:cell(origem)="{ value }">
                    {{ originLabel(value) }}
                </template>

                <template v-slot:cell(examesGrupos)="{ item, value }">
                    <rkm-button
                        v-if="splitItems(value).length"
                        @click="$refs.ExamesGruposModal.open(item)"
                        color="info"
                        small
                        :title="`Visualizar ${splitItems(value).length} exame(s)/grupo(s)`"
                    >
                        <rkm-icon icon="eye" />
                    </rkm-button>

                    <span v-else>-</span>
                </template>

                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        v-if="item.podeConfirmar"
                        @click="confirm(item)"
                        authorize="confirmar-exame-laboratorial-confirmacao-coleta"
                        color="success"
                        small
                        title="Confirmar"
                    >
                        <rkm-icon icon="check" />
                    </rkm-button>
                </template>

            </rkm-data-table>

            <div class="legenda mt-2">
                <span class="legenda-cor"></span>
                Confirmado
                <span class="legenda-cor legenda-cor-cancelado ml-3"></span>
                Cancelado
            </div>
        </rkm-area>

        <ModalPreferenciaImpressao
            @orientation="orientation = $event"
            ref="ModalPreferenciaImpressao"
        />

        <ExamesGruposModal ref="ExamesGruposModal" />
    </div>
</template>

<script>
import moment from 'moment'
import ModalPreferenciaImpressao from '@/components/geral/impressao/Modal'
import ExamesGruposModal from './ExamesGruposModal'
import { error, file, formats, repository, stringCase } from '@/mixins'
import { mapActions } from 'vuex'

export default {
    components: { ExamesGruposModal, ModalPreferenciaImpressao },
    computed: {
        allOptionalColumnsSelected() {
            return this.optionalColumns.every(column => this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !column.fixed)
        },
        requestParams() {
            if (!this.model) return {}

            return {
                periodo: {
                    start: moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidade_solicitante_id: this.model.unidadeSolicitanteId,
                infraestrutura_id: this.model.infraestruturaId,
                coleta_infraestrutura_id: this.model.coletaInfraestruturaId,
                exame_grupo: this.model.exameGrupo,
                situacao: this.model.situacao,
            }
        },
        totalizerItems() {
            return [
                { key: 'pacientes', label: 'Pacientes' },
                { key: 'confirmados', label: 'Confirmados' },
                { key: 'naoConfirmados', label: 'Não confirmados' },
                { key: 'cancelados', label: 'Cancelados' },
            ]
        },
        validations() {
            return { periodo: { required: true } }
        },
        visibleFields() {
            return this.columns.filter(column => column.fixed || this.selectedColumns.includes(column.key))
        },
    },
    created() {
        this.repositories = {
            confirmacaoColeta: this.getConectaSusRepository('exameLaboratorial.confirmacaoColeta'),
            impressao: this.getConectaSusRepository('impressao.exameLaboratorial'),
        }

        this.load()
    },
    data() {
        return {
            columns: [
                { key: 'paciente', label: 'Paciente', fixed: true, sortable: true },
                { key: 'telefone', label: 'Telefone', fixed: true },
                { key: 'data', label: 'Data', fixed: true, sortable: true, formatter: value => this.formatDate(value) },
                { key: 'horario', label: 'Horário', fixed: true, sortable: true },
                { key: 'unidadeColeta', label: 'Unidade de coleta', fixed: true, sortable: true },
                { key: 'localAtendimento', label: 'Local de atendimento', fixed: true, sortable: true },
                { key: 'situacaoConfirmacao', label: 'Situação da confirmação', fixed: true, sortable: true },
                { key: 'idade', label: 'Idade' },
                { key: 'prontuario', label: 'Prontuário' },
                { key: 'agendamentoNumero', label: 'Número do pedido' },
                { key: 'medicoSolicitante', label: 'Médico solicitante' },
                { key: 'unidadeSolicitante', label: 'Unidade solicitante' },
                { key: 'localColeta', label: 'Local de coleta' },
                { key: 'examesGrupos', label: 'Exames/Grupos' },
                { key: 'origem', label: 'Origem da confirmação' },
                { key: 'actions', label: 'Ação', class: 'col-min text-center', fixed: true },
            ],
            form: null,
            model: null,
            orientation: 'landscape',
            searched: false,
            optionsTimeout: null,
            selectedColumns: [],
            totalizadores: {
                pacientes: 0,
                confirmados: 0,
                naoConfirmados: 0,
                cancelados: 0,
            },
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        selectAllOptionalColumns(selected) {
            this.selectedColumns = selected
                ? this.optionalColumns.map(column => column.key)
                : []
        },
        load() {
            this.setLoading()
            this.repositories.confirmacaoColeta.create()
                .then(response => {
                    this.form = response.data.form
                    this.model = response.data.model
                    this.model.periodo.start = moment(this.model.periodo.start, 'DD/MM/YYYY').toDate()
                    this.model.periodo.end = moment(this.model.periodo.end, 'DD/MM/YYYY').toDate()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadDependentOptions() {
            this.model.infraestruturaId = null
            this.model.coletaInfraestruturaId = null
            this.reloadOptions()
        },
        reloadOptions() {
            this.repositories.confirmacaoColeta.list(this.requestParams)
                .then(response => {
                    this.form.locaisAtendimento = response.data.locaisAtendimento
                    this.form.locaisColeta = response.data.locaisColeta
                    this.form.examesGrupos = response.data.examesGrupos
                })
                .catch(this.handleError)
        },
        search(resetPage = true) {
            this.searched = true
            if (resetPage) this.$refs.DataTable.currentPage = 1
            this.$nextTick(() => this.$refs.DataTable.refresh())
            this.refreshTotals()
        },
        refreshTotals() {
            this.repositories.confirmacaoColeta.totalizadores(this.requestParams)
                .then(response => {
                    this.totalizadores = response.data
                })
                .catch(this.handleError)
        },
        reset() {
            const today = moment().startOf('day').toDate()
            this.model.periodo = { start: today, end: today }
            this.model.unidadeSolicitanteId = null
            this.model.infraestruturaId = null
            this.model.coletaInfraestruturaId = null
            this.model.exameGrupo = null
            this.model.situacao = null
            this.searched = false
            this.totalizadores = { pacientes: 0, confirmados: 0, naoConfirmados: 0, cancelados: 0 }
            this.$refs.Form.resetValidator()
            this.$nextTick(() => this.$refs.DataTable.clearTable())
        },
        print() {
            this.$refs.ModalPreferenciaImpressao.open()
            this.$refs.ModalPreferenciaImpressao.$once('completed', () => {
                const columns = this.visibleFields
                    .filter(column => !['actions', 'examesGrupos'].includes(column.key))
                    .map(column => ({ key: this.camelToSnake(column.key), label: column.label }))
                const request = {
                    ...this.requestParams,
                    selected_cols: columns,
                    orientation: this.orientation,
                }

                this.setLoading()
                this.repositories.impressao.confirmacaoColeta(request)
                    .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        confirm(item) {
            const callback = () => this.repositories.confirmacaoColeta.confirmar(item.agendamentoId)
                .then(({ data }) => {
                    item.situacaoConfirmacao = data ? data.situacao : 'confirmado'
                    item.origem = data ? data.origem : 'manual'
                    item.respondidoEm = data ? data.respondidoEm : null
                    item.podeConfirmar = false
                    this.refreshTotals()
                })
                .catch(this.handleError)

            this.showConfirmation(`Confirma a presença de ${item.paciente}?`, callback)
        },
        situationLabel(value) {
            return {
                confirmado: 'Confirmado',
                naoConfirmado: 'Não confirmado',
                nao_confirmado: 'Não confirmado',
                cancelado: 'Cancelado',
            }[value] || value
        },
        originLabel(value) {
            return {
                whatsapp: 'WhatsApp',
                conectasusCidadao: 'App ConectaSUS Cidadão',
                conectasus_cidadao: 'App ConectaSUS Cidadão',
                manual: 'Manual',
            }[value] || ''
        },
        splitItems(value) {
            return value ? value.split('#') : []
        },
        rowClass(item, type) {
            if (!item || type !== 'row') return null

            return {
                confirmado: 'table-success',
                cancelado: 'table-danger',
            }[item.situacaoConfirmacao] || null
        },
    },
    mixins: [error, file, formats, repository, stringCase],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                if (!value || !value.start || !value.end || !this.form) return

                clearTimeout(this.optionsTimeout)
                this.optionsTimeout = setTimeout(() => {
                    this.model.unidadeSolicitanteId = null
                    this.model.infraestruturaId = null
                    this.model.coletaInfraestruturaId = null
                    this.model.exameGrupo = null
                    this.reloadOptions()
                }, 400)
            },
        },
    },
}
</script>

<style scoped>
.totalizador {
    align-items: center;
    background: var(--light);
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 1rem;
}

.totalizador strong {
    font-size: 1.25rem;
}

.legenda {
    align-items: center;
    display: flex;
    gap: 0.5rem;
}

.legenda-cor {
    background: #d4edda;
    border: 1px solid #b1dfbb;
    display: inline-block;
    height: 1rem;
    width: 1.5rem;
}
.legenda-cor-cancelado {
    background: #f8d7da;
    border-color: #f5c6cb;
}
</style>
